import { Close, ListAlt, North, South } from '@mui/icons-material';
import { Avatar, Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { Store, StoreLog } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { Lang, useLocale } from '../../contexts/locale.context';
import { useCollection } from '../../utils/api';
import currency from '../../utils/currency';
import getQntLabel from '../../utils/getQntLabel';
import useMedia from '../../utils/useMedia';
import { Column } from '../DataTable';
import Image from '../Image';
import HistoryModal from './HistoryModal';

type Props = {
  store: DbDocument<Store>;
  productId?: string;
};

type Row = DbDocument<StoreLog>;

export const getLogComment = (log: Row, lang: Lang): string => {
  if (log.info.type === 'addToOrder') {
    return `${log.info.dishName} ${t.added_to_order[lang]} #${log.info.number}`;
  }
  if (log.info.type === 'removeFromOrder') {
    return `${log.info.dishName} ${t.removed_from_order[lang]} #${log.info.number}`;
  }
  if (log.info.type === 'changeOrderItem') {
    return `${t.modified[lang]} "${log.info.dishName}" (${t.order[lang]} #${log.info.number})`;
  }
  if (log.info.type === 'makeSemiProduct') {
    return `${t.semifinished_product_made[lang]} ${log.info.name}`;
  }
  if (log.info.type === 'transfer') {
    return `${t.moved_from[lang]} ${log.info.from.name} ${t.to[lang]} ${log.info.to.name}`;
  }
  if (log.info.type === 'supply') {
    return `${t.supply[lang]} #${log.info.number}`;
  }
  if (log.info.type === 'inventory') {
    return `${t.inventory[lang]}`;
  }
  if (log.info.type === 'selfOrder') {
    return `${t.order[lang]} #${log.info.number}`;
  }
  return '';
};

const InventoryModal = ({ log, onClose }: { log: Row; onClose: () => void }) => {
  const { isMobile } = useMedia();
  const { lang } = useLocale();
  const { cafe } = useAdmin();
  const { ingredientsMap } = useCafe();

  const isInventory = log.info.type === 'inventory';

  const totalCost = useMemo(
    () =>
      'costDiff' in log.info && log.info.costDiff
        ? Object.values(log.info.costDiff).reduce((acc, cost) => acc + cost, 0)
        : 0,
    [log.info]
  );

  const title = `${getLogComment(log, lang)} (${format(new Date(log.time), 'dd.MM.yy - HH:mm')})`;

  return (
    <Dialog open onClose={onClose} maxWidth={isInventory ? 'md' : 'sm'} fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {title}
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box>
          {isInventory && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <Typography variant="body2">{t.component[lang]}</Typography>
              </Grid>

              <Grid item xs={4} sm={2}>
                <Typography variant="body2">{t.before[lang]}</Typography>
              </Grid>

              <Grid item xs={4} sm={2}>
                <Typography variant="body2">{t.after[lang]}</Typography>
              </Grid>

              <Grid item xs={3} sm={2}>
                <Typography variant="body2">{t.difference[lang]}</Typography>
              </Grid>

              <Grid item xs={1} />
            </Grid>
          )}

          {!isInventory &&
            Object.entries(log.diff).map(([id, diff]) => {
              const i = ingredientsMap[id];

              if (!i) {
                return null;
              }

              return (
                <Box
                  key={id}
                  sx={{ p: 0.5, borderRadius: 1, ':hover': { bgcolor: (t) => `${t.palette.primary.light}20` } }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Image src={i.imageUrl} />
                        <Typography>{i.name}</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4}>
                      {!!diff && (
                        <Typography color={diff > 0 ? (t) => t.palette.info.dark : (t) => t.palette.error.dark}>
                          {getQntLabel(diff, i, lang, false)}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

          {'newState' in log.info &&
            Object.entries(log.info.newState).map(([id, newQnt]) => {
              const i = ingredientsMap[id];
              const diff = log.diff[id] || 0;

              if (!i || log.info.type !== 'inventory') {
                return null;
              }

              return (
                <Box
                  key={id}
                  sx={{ p: 0.5, borderRadius: 1, ':hover': { bgcolor: (t) => `${t.palette.primary.light}20` } }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Image src={i.imageUrl} />
                        <Typography>{i.name}</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Typography>{getQntLabel(log.info.oldState[id] || 0, i, lang, false)}</Typography>
                    </Grid>

                    <Grid item xs={4} sm={2}>
                      <Typography>{getQntLabel(newQnt, i, lang, false)}</Typography>
                    </Grid>

                    <Grid item xs={3} sm={2}>
                      {!!diff && (
                        <Typography
                          variant="body2"
                          color={diff > 0 ? (t) => t.palette.success.dark : (t) => t.palette.error.dark}
                        >
                          {getQntLabel(diff, i, lang, false)}
                        </Typography>
                      )}
                      {!!diff && (
                        <Typography
                          variant="body2"
                          color={diff > 0 ? (t) => t.palette.success.main : (t) => t.palette.error.dark}
                        >
                          {currency(log.info.costDiff?.[id] || 0, cafe?.currency, true)}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={1} sm={1}>
                      {diff > 0 && <North color="success" />}
                      {diff < 0 && <South color="error" />}
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
        </Box>
        {!!totalCost && (
          <Typography
            variant="h6"
            sx={{
              textAlign: 'right',
              color: (t) => (totalCost > 0 ? t.palette.success.main : t.palette.error.main),
            }}
          >
            {t.total[lang]}: {totalCost > 0 ? '+' : ''}
            {currency(totalCost, cafe?.currency)}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StoreHistory = ({ store, productId }: Props) => {
  const [dateRange, setDateRange] = useState<{ end: number; start: number } | null>(null);
  const { lang } = useLocale();
  const { cafeSlug } = useAdmin();
  const { workersMap, ingredientsMap } = useCafe();
  const [log, setLog] = useState<Row | null>(null);

  const { data } = useCollection<StoreLog>(dateRange ? `cafes/${cafeSlug}/storeLogs/` : null, {
    where: ['storeId', '==', store.id],
    where2: ['time', '<', dateRange?.end || 0],
    where3: ['time', '>', dateRange?.start || 0],
    orderBy: ['time', 'desc'],
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'date',
        title: t.date[lang],
        align: 'left',
        render: (r: Row) => <Typography>{format(new Date(r.time), 'HH:mm')}</Typography>,
      },
      {
        name: 'author',
        title: t.author[lang],
        align: 'left',
        render: (r: Row) => (
          <Stack direction="row" alignItems="center" spacing={{ xs: 1 }}>
            <Avatar src={workersMap[r.workerId]?.photoUrl} />
            <span>{workersMap[r.workerId]?.name || r.workerName}</span>
          </Stack>
        ),
      },
      {
        name: 'comment',
        title: t.comment[lang],
        align: 'left',
        render: (r: Row) => <Typography>{getLogComment(r, lang)}</Typography>,
      },
      {
        name: 'qnt',
        title: t.quantity[lang],
        align: 'left',
        render: productId
          ? (r: Row) => getQntLabel(r.diff[productId] || 0, ingredientsMap[productId], lang, false)
          : undefined,
      },
      {
        name: 'action',
        title: '',
        align: 'left',
        render: (r: Row) => (
          <IconButton onClick={() => setLog(r)}>
            <ListAlt />
          </IconButton>
        ),
      },
    ],
    [ingredientsMap, lang, productId, workersMap]
  );

  const filteredRows = useMemo(
    () =>
      productId ? data?.filter((r) => Object.entries(r.diff).some(([id, qnt]) => id === productId && !!qnt)) : data,
    [data, productId]
  );

  return (
    <>
      <HistoryModal
        name={productId ? ingredientsMap[productId]?.name || '' : store.name}
        columns={columns}
        rows={filteredRows || []}
        setDateRange={setDateRange}
      />

      {log && <InventoryModal log={log} onClose={() => setLog(null)} />}
    </>
  );
};

export default StoreHistory;
